import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { BusinessPlan } from '../../types/businessPlan';
import { buildSlides } from '../../lib/pitchDeckSlides';

interface Props {
  plan: BusinessPlan;
  onClose: () => void;
}

export default function PitchDeckModal({ plan, onClose }: Props) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = buildSlides(plan);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrentSlide((p) => Math.min(slides.length - 1, p + 1));
      if (e.key === 'ArrowLeft') setCurrentSlide((p) => Math.max(0, p - 1));
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, slides.length]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const isFirst = currentSlide === 0;
  const isLast = currentSlide === slides.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal panel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.2 }}
        className="relative flex w-full max-w-3xl flex-col rounded-2xl shadow-2xl"
        style={{ backgroundColor: '#111827', maxHeight: '92vh' }}
      >
        {/* Header */}
        <div
          className="flex shrink-0 items-center justify-between px-5 py-3"
          style={{ borderBottom: '1px solid #1f2937' }}
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#d94f1e' }}>
              Pitch Deck
            </p>
            <h2 className="text-sm font-semibold text-white">
              {plan.businessName || 'My Business'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Slide — aspect-video, full-bleed */}
        <div className="px-4 pt-4">
          <div className="relative aspect-video w-full overflow-hidden rounded-xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="h-full w-full"
              >
                {slides[currentSlide].content}
              </motion.div>
            </AnimatePresence>

            {/* Prev arrow */}
            {!isFirst && (
              <button
                onClick={() => setCurrentSlide((p) => p - 1)}
                className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-sm text-white"
                style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
              >
                ←
              </button>
            )}
            {/* Next arrow */}
            {!isLast && (
              <button
                onClick={() => setCurrentSlide((p) => p + 1)}
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-sm text-white"
                style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
              >
                →
              </button>
            )}
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="flex justify-center gap-1 overflow-x-auto px-4 py-3">
          {slides.map((slide, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              title={slide.title}
              className="flex shrink-0 flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 transition-all"
              style={{
                backgroundColor: i === currentSlide ? 'rgba(255,255,255,0.1)' : 'transparent',
                borderBottom: i === currentSlide ? '2px solid #d94f1e' : '2px solid transparent',
              }}
            >
              <span className="text-sm leading-none">{slide.emoji}</span>
              <span
                className="max-w-[44px] truncate text-[9px] font-medium"
                style={{ color: i === currentSlide ? '#e5e7eb' : '#6b7280' }}
              >
                {slide.title}
              </span>
            </button>
          ))}
        </div>

        {/* Footer nav */}
        <div
          className="flex shrink-0 items-center justify-between px-5 py-3"
          style={{ borderTop: '1px solid #1f2937' }}
        >
          <button
            onClick={() => setCurrentSlide((p) => Math.max(0, p - 1))}
            disabled={isFirst}
            className="rounded-lg border px-4 py-1.5 text-sm font-medium text-gray-400 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"
            style={{ borderColor: '#374151' }}
          >
            ← Prev
          </button>
          <span className="text-xs text-gray-600">
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => setCurrentSlide((p) => Math.min(slides.length - 1, p + 1))}
            disabled={isLast}
            className="rounded-lg px-4 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-30"
            style={{ backgroundColor: '#d94f1e' }}
          >
            Next →
          </button>
        </div>
      </motion.div>
    </div>
  );
}
